import axios from "axios";
import { REACT_APP_PYJOBAPI_URI } from "../../env";
import { FUND_PARSE_JOB_NAME } from "./fundParseDefaults";
import { FundParseJobHistoryResult, FundParseJobRunResponse, FundParseOutputFile } from "./fundParseTypes";
import { normalizeFundParseJobStatus, sleep } from "./fundParseUtils";

const TERMINAL_JOB_STATUSES = new Set(["completed", "failed"]);

interface FundParseOutputFileIndexEntry {
    fileName: string;
    mimeType?: string;
    sizeBytes?: number;
}

function buildFundParseOutputFileUrl(jobId: string, fileName: string): string {
    const encodedPath = fileName
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/");
    return `${REACT_APP_PYJOBAPI_URI}/jobs/${FUND_PARSE_JOB_NAME}/history/${encodeURIComponent(jobId)}/files/${encodedPath}`;
}

function getOutputFileIndex(output: unknown): FundParseOutputFileIndexEntry[] {
    if (!output || typeof output !== "object") {
        return [];
    }
    const record = output as Record<string, unknown>;
    const index = record.output_file_index;
    if (!Array.isArray(index)) {
        return [];
    }
    return index.filter(
        (item): item is FundParseOutputFileIndexEntry =>
            typeof item === "object" &&
            item !== null &&
            typeof (item as FundParseOutputFileIndexEntry).fileName === "string"
    );
}

function outputFilesNeedHydration(output: unknown): boolean {
    if (!output || typeof output !== "object") {
        return false;
    }
    const record = output as Record<string, unknown>;
    const existingFiles = record.output_files;
    if (Array.isArray(existingFiles) && existingFiles.length > 0) {
        const hasEmbeddedPayload = existingFiles.every(
            (item) =>
                typeof item === "object" &&
                item !== null &&
                typeof (item as FundParseOutputFile).base64 === "string" &&
                (item as FundParseOutputFile).base64.length > 0
        );
        if (hasEmbeddedPayload) {
            return false;
        }
    }
    return getOutputFileIndex(output).length > 0;
}

export async function fetchFundParseJobOutputFile(jobId: string, fileName: string): Promise<FundParseOutputFile> {
    const response = await axios.get<FundParseOutputFile>(buildFundParseOutputFileUrl(jobId, fileName), {
        withCredentials: true,
        headers: {
            Accept: "application/json"
        }
    });
    return response.data;
}

export async function hydrateFundParseJobHistoryOutputFiles(
    jobId: string,
    history: FundParseJobHistoryResult
): Promise<FundParseJobHistoryResult> {
    if (!outputFilesNeedHydration(history.output)) {
        return history;
    }

    const fileIndex = getOutputFileIndex(history.output);
    const outputFiles = await Promise.all(fileIndex.map((item) => fetchFundParseJobOutputFile(jobId, item.fileName)));

    const outputRecord =
        history.output && typeof history.output === "object" ? (history.output as Record<string, unknown>) : {};

    return {
        ...history,
        output: {
            ...outputRecord,
            output_files: outputFiles
        }
    };
}

export async function runFundParseJob(args: Record<string, unknown>): Promise<FundParseJobRunResponse> {
    const response = await axios.post<FundParseJobRunResponse>(
        `${REACT_APP_PYJOBAPI_URI}/jobs/${FUND_PARSE_JOB_NAME}/run`,
        args,
        {
            withCredentials: true,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
            }
        }
    );
    return response.data;
}

export async function getFundParseJobHistory(jobId: string): Promise<FundParseJobHistoryResult | null> {
    try {
        const response = await axios.get<FundParseJobHistoryResult>(
            `${REACT_APP_PYJOBAPI_URI}/jobs/${FUND_PARSE_JOB_NAME}/history/${jobId}`,
            {
                withCredentials: true,
                headers: {
                    Accept: "application/json"
                }
            }
        );
        const result = response.data;
        const normalizedStatus = normalizeFundParseJobStatus(result.status);
        if (normalizedStatus) {
            result.status = normalizedStatus;
        }
        return hydrateFundParseJobHistoryOutputFiles(jobId, result);
    } catch (error) {
        // pyjobapi returns 404 until the async job finishes and appends to history.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
        }
        throw error;
    }
}

export async function pollFundParseJobUntilDone(
    jobId: string,
    options?: { intervalMs?: number; timeoutMs?: number; onUpdate?: (result: FundParseJobHistoryResult) => void }
): Promise<FundParseJobHistoryResult> {
    const intervalMs = options?.intervalMs ?? 1500;
    const timeoutMs = options?.timeoutMs ?? 10 * 60 * 1000;
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
        const result = await getFundParseJobHistory(jobId);
        if (result === null) {
            if (options?.onUpdate) {
                options.onUpdate({ status: "active" });
            }
            await sleep(intervalMs);
            continue;
        }
        if (options?.onUpdate) {
            options.onUpdate(result);
        }
        if (result.status && TERMINAL_JOB_STATUSES.has(result.status)) {
            return result;
        }
        await sleep(intervalMs);
    }

    throw new Error("Timed out while waiting for the job to finish.");
}
